package com.macrofocus.eflux.ui

import com.macrofocus.eflux.LagUnit
import com.macrofocus.eflux.time.TimeTracker
import i18next.useTranslation
import js.array.ReadonlyArray
import js.objects.unsafeJso
import kotlinx.coroutines.*
import mui.icons.material.PlayCircleOutlineOutlined
import mui.icons.material.StopCircleOutlined
import mui.material.*
import mui.material.Size
import mui.system.sx
import org.mkui.react.useProperty
import react.*
import react.dom.events.MouseEvent
import react.dom.events.SyntheticEvent
import web.cssom.*
import web.events.Event
import web.html.HTMLElement

enum class AnimationState {
    STOPPED,
    WILL_START,
    STARTED,
    WILL_STOP
}

sealed external interface Mark {
    var value: Number?
    var label: String?
}

external interface TimeSliderProps : PropsWithChildren {
    var timeTracker: TimeTracker
    var timeUnitChooser: Boolean
    var valueLabelDisplay: mui.system.Union?
    var width: Width?
}

val TimeSlider = FC<TimeSliderProps> { props ->
    val DEBUG = false

    val (t, _) = useTranslation()

//    val (_, setMasterDateTime) = props.timeTracker.masterDateTime.toState()
//    val (getLatestDateTime, _) = props.timeTracker.latestDateTime.toState()
    val (getLag, setLag) = useProperty(props.timeTracker.masterLagProperty)
    val (getLagUnit, setLagUnit) = useProperty(props.timeTracker.lagUnitProperty)
    val (getLocalLag, setLocalLag) = useProperty(props.timeTracker.displayLagProperty)

    // If there is a Job set, but it is cancelled, then the state is considered as paused
    val (getJob, setJob) = useState<Job?>(null)
    val (getAnimationState, setAnimationState) = useState<AnimationState>(AnimationState.STOPPED)

    useEffect(getLag) { setLocalLag(getLag) }

    Box {
        sx {
            display = Display.inlineFlex
        }

        IconButton {
            sx {
                marginRight = 5.px
            }


    //        classes(classes.menuButton)
    //        color = IconButtonColor.inherit
    //        edge = IconButtonEdge.start
            onClick = {
                startStopPlayback(setAnimationState, getJob, setJob, getLagUnit, getLocalLag, props)
            }

            disabled = getAnimationState == AnimationState.WILL_STOP || getAnimationState == AnimationState.WILL_START

            if(getAnimationState == AnimationState.STARTED || getAnimationState == AnimationState.WILL_START) StopCircleOutlined {} else PlayCircleOutlineOutlined {}
        }

        val timeMarks: ReadonlyArray<Mark> = arrayOf(
            unsafeJso { value = -0.00 * 5; label = "0" },
            unsafeJso { value = -72.00 * 5; label = "-6" },
            unsafeJso { value = -144.00 * 5; label = "-12" },
            unsafeJso { value = -216.00 * 5; label = "-18" },
            unsafeJso { value = -288.00 * 5; label = "-24" },
        )

        Slider {
            sx = unsafeJso {
                this.width = props.width
                marginRight = 10.px
                marginTop = 6.px
            }

            marks = timeMarks

            color = SliderColor.secondary

//            ariaLabel = "Lag"
            value = getLocalLag
    //                        getAriaValueText={valuetext}
            this.valueLabelDisplay = props.valueLabelDisplay
            step = getLagUnit.step
            marks
            min = -(getLagUnit.max / getLagUnit.unit)
//            when (getLagUnit) {
//                LagUnit.minutes -> -24 * 60
//                LagUnit.hours -> -24 * 7
//                LagUnit.days -> -180
//                LagUnit.weeks -> -156
//            }
            max = 0.0
//            when (getLagUnit) {
//                LagUnit.minutes -> 0
//                LagUnit.hours -> 0
//                LagUnit.days -> 0
//                LagUnit.weeks -> 0
//            }
            size = Size.small
            onChange = { event: Event, current: dynamic, activeThumb: Number ->
                event.stopImmediatePropagation()
                event.stopPropagation()
                event.preventDefault()
                if(DEBUG) println("onChange: $current $event")
                if(DEBUG) println("onChange2: ${event.asDynamic().nativeEvent}")
                if (current is Number) {
                    pausePlayback(getJob)
                    props.timeTracker.timeSliderMoved(current.toInt())
                }
            }
            onChangeCommitted = { _: SyntheticEvent<*, *>, current: dynamic ->
                if(DEBUG) println("onChangeCommitted: $current")
                if (current is Number) {
//                    setLocalLag(current.toInt())
//                    setLag(current.toInt())
//                    setMasterDateTime(getLatestDateTime + (getLagUnit.unit * current.toInt()))
                    props.timeTracker.timeSliderReleased(current.toInt())
                    resumePlayback(setAnimationState, getJob, getLagUnit, setJob, getLocalLag, props)
                }
            }
        }


        if(props.timeUnitChooser) {
            ToggleButtonGroup {
                orientation = Orientation.vertical
                size = Size.small

                for (shiftUnit in LagUnit.values().sliceArray(0..1)) {
                    ToggleButton {
                        disabled = getLagUnit != shiftUnit && getJob != null
                        size = Size.small
                        sx = unsafeJso {
                            textTransform = None.none
                            fontSize = FontSize.xxSmall
                            padding = 1.px
                            marginTop = -3.px
                        }

                        selected = getLagUnit == shiftUnit
                        onClick = { event: MouseEvent<HTMLElement, *>, value: dynamic ->
                            setLocalLag(0)
                            setLag(0)
                            setLagUnit(shiftUnit)
                        }
                        +shiftUnit.name
                    }
                }
            }
            ToggleButtonGroup {
                orientation = Orientation.vertical
                size = Size.small

                for (shiftUnit in LagUnit.values().sliceArray(2..3)) {
                    ToggleButton {
                        disabled = getLagUnit != shiftUnit && getJob != null
                        size = Size.small
                        sx = unsafeJso {
                            textTransform = None.none
                            fontSize = FontSize.xxSmall
                            padding = 1.px
                            marginTop = -3.px
                        }

                        selected = getLagUnit == shiftUnit
                        onClick = { event: MouseEvent<HTMLElement, *>, value: dynamic ->
                            setLocalLag(0)
                            setLag(0)
                            setLagUnit(shiftUnit)
                        }
                        +shiftUnit.name
                    }
                }
            }
        }
    }
}

private fun startStopPlayback(
    setAnimationState: StateSetter<AnimationState>,
    getJob: Job?,
    setJob: StateSetter<Job?>,
    getLagUnit: LagUnit,
    getLocalLag: Int,
    props: TimeSliderProps
) {
    val job = getJob
    if (job != null && !job.isCancelled) {
        setAnimationState(AnimationState.WILL_STOP)
        stopPlayback(job, setJob)
        setAnimationState(AnimationState.STOPPED)
    } else {
        setAnimationState(AnimationState.WILL_START)
        startPlayback(setAnimationState, getLagUnit, setJob, getLocalLag, props)
        setAnimationState(AnimationState.STARTED)
    }
}

private fun startPlayback(
    setAnimationState: StateSetter<AnimationState>,
    getLagUnit: LagUnit,
    setJob: StateSetter<Job?>,
    getLocalLag: Int,
    props: TimeSliderProps
) {
    val ms = if (getLagUnit == LagUnit.minutes) 1000L else 1000L
    println("Starting animation with 5 minutes = $ms ms")
    setJob(GlobalScope.launch {
        var current = getLocalLag
        while (true) {
            delay(ms)
            current = if (current < 0) current + getLagUnit.step else
                -(getLagUnit.max / getLagUnit.unit).toInt()
            //                                println("Next date: " + next.toRawString())
            //                                masterDateTime.value = next.toDateTime()
//                            println("Shifting to $current")
//                            setLocalLag(current)
//                            setLag(current)
            val stopRequested = props.timeTracker.stepAnimation(current)
            if(stopRequested) {
                setAnimationState(AnimationState.WILL_STOP)
                cancel()
                setJob(null)
                setAnimationState(AnimationState.STOPPED)
            }
        }
    })
}

private fun stopPlayback(job: Job, setJob: StateSetter<Job?>) {
    job.cancel()
    setJob(null)
}

private fun pausePlayback(job: Job?) {
    job?.cancel()
}

private fun resumePlayback(
    setAnimationState: StateSetter<AnimationState>,
    job: Job?,
    getLagUnit: LagUnit,
    setJob: StateSetter<Job?>,
    getLocalLag: Int,
    props: TimeSliderProps
) {
    if(job != null && job.isCancelled) {
        startPlayback(setAnimationState, getLagUnit, setJob, getLocalLag, props)
    }
}